/**
 * ApprovalPanel — Doc 21 (Approval Workflow), Fase 2.c.
 *
 * Panel que se monta dentro del viewer del artifact cuando el status es
 * `in_review`. Renderiza el progreso multi-stage del workflow:
 *
 *   - Stages numeradas con su nombre (i18n) y estado agregado (pending /
 *     done / rejected / cancelled / upcoming).
 *   - Por cada stage, lista de aprobadores con su decisión individual y
 *     comentario (si aplica).
 *   - Botones "Aprobar / Rechazar" si el current user tiene una approval
 *     `is_active=true` y `status='pending'` en el stage activo.
 *
 * Backwards-compat con artifacts pre-Doc 21: si las approvals no traen
 * `stage_order`, se renderiza un solo stage 0 con todas las approvals
 * en paralelo (el flow legacy).
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Check,
  Clock,
  Loader2,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  X,
  XCircle,
} from 'lucide-react'

import { ApiError } from '../../api/client'
import { ConfirmDialog } from '../ConfirmDialog'
import {
  decideApproval,
  getApprovalHistory,
  listVersionApprovals,
  type Approval,
  type ApprovalStatus,
} from '../../api/artifacts'
import { useVersionGroundings } from '../../api/citations'
import { listVersionComments } from '../../api/comments'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { pickLocalized, toIntlLocale } from '../../i18n/config'
import { canViewProjectInternals, canWriteInTenant, isExternal } from '../../lib/tiers'
import { pushToast } from '../../stores/toastStore'
import { VersionObservationTrail } from '../ai/GroundingFindings'
import {
  sectionInstanceTitle,
  type TemplateI18nSection,
} from './official-viewer/section_types'

// ─── Types locales ──────────────────────────────────────────────────────────
type Locale = 'es' | 'en'

interface StageGroup {
  stage_order: number
  stage_name: string
  approvals: Approval[]
  /** Estado agregado del stage. */
  state: 'done' | 'rejected' | 'cancelled' | 'active' | 'upcoming'
}

// ─── Component ──────────────────────────────────────────────────────────────
export interface ApprovalPanelProps {
  artifactId: string
  versionId: string
  /** Doc 21 Fase 5 — review_cycle activo del version. Lo usa el panel
   *  para filtrar comments del cycle correcto al derivar el estado
   *  del gate de canon (Fase 6). */
  reviewCycle: number
  /** Doc 21 Fase 6 — si el artifact ya es canon, el banner de
   *  "comments pendientes" no aplica (idempotencia visual). */
  isCanon: boolean
  /** Para identificar al current user en la lista de approvers (badge "Tú")
   *  y decidir si pinto los botones de Aprobar/Rechazar. */
  currentUserId: string | null
  /** Locale para nombres i18n del stage. Si null, infiere del i18n.language. */
  locale?: Locale
  /** OT-0049 — títulos localizados de las secciones, para agrupar por
   *  sección el rastro de la verificación de IA. */
  templateI18nMap: Map<string, TemplateI18nSection>
  /** Callback que el caller puede usar para refrescar otros datos
   *  derivados (ej. el banner de status del artifact, la query del
   *  artifact). */
  onDecisionRecorded?: () => void
}

export function ApprovalPanel({
  artifactId,
  versionId,
  reviewCycle,
  isCanon,
  currentUserId,
  locale: localeProp,
  templateI18nMap,
  onDecisionRecorded,
}: ApprovalPanelProps) {
  const { t, i18n } = useTranslation('projects')
  const { accessToken } = useAccessToken()
  const { me } = useMe()
  // Aprobar o rechazar es firmar: con la organización en solo lectura el
  // backend lo niega, así que no se ofrece (OT-0073 §4.4).
  const canDecide = canWriteInTenant(me?.tenant?.access)
  const queryClient = useQueryClient()
  // OT-0017 — el `external` no resuelve los comentarios del equipo (solo los
  // suyos): su salida es esperar o rechazar, y los textos lo dicen así.
  const viewerIsExternal = isExternal(me?.role)
  const locale: Locale =
    localeProp ?? (i18n.language === 'en' ? 'en' : 'es')

  const approvalsQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', versionId, 'approvals'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listVersionApprovals(accessToken, artifactId, versionId)
    },
    enabled: !!accessToken,
    staleTime: 15_000,
  })

  // OT-0049 — rastro de la verificación de IA: qué observaciones dejó y
  // quién las revisó. El autor ya las revisó para poder enviar a revisión.
  // Es derivado de las fuentes del proyecto: solo se pide con un tier
  // interno confirmado por `/me` (OT-0017).
  const groundingsQuery = useVersionGroundings({
    artifactId,
    versionId,
    enabled: canViewProjectInternals(me?.role),
  })

  const stages = useMemo<StageGroup[]>(() => {
    if (!approvalsQuery.data) return []
    return groupApprovalsByStage(approvalsQuery.data, locale, t)
  }, [approvalsQuery.data, locale, t])

  // Issue #3 — ¿el current user tiene una decisión pendiente suya en el
  // ciclo activo? Si sí, auto-expand el panel al primer render para que
  // no tenga que abrirlo manualmente para actuar. Después de ese
  // primer expand, el state queda manual (no se re-colapsa solo).
  const hasPendingActionForMe = useMemo(() => {
    if (!currentUserId || !approvalsQuery.data) return false
    return approvalsQuery.data.some(
      (a) =>
        a.approver_user_id === currentUserId &&
        a.status === 'pending' &&
        (a.is_active === undefined || a.is_active === null || a.is_active),
    )
  }, [approvalsQuery.data, currentUserId])

  // El expand "manual" del usuario gana sobre el auto-expand. Usamos
  // tri-state: null = no decidió, true = abierto, false = cerrado.
  // Cuando `userToggle === null`, el auto-expand toma efecto si
  // hasPendingActionForMe. Cualquier interacción del usuario fija el
  // estado y deja de seguir el auto.
  const [userToggle, setUserToggle] = useState<boolean | null>(null)
  const isExpanded = userToggle ?? hasPendingActionForMe

  // Doc 21 Fase 6 — la query de comments alimenta DOS gates:
  //   1. El banner "canon bloqueado" cuando todos aprobaron pero faltan
  //      comments resueltos (soft-block legacy).
  //   2. El hard-block del botón Aprobar: si hay comments abiertos del
  //      cycle activo, el reviewer no puede emitir el voto hasta que
  //      se resuelvan. Eso obliga a cargar la query antes — desde que
  //      el panel se monta, no solo cuando los stages están done.
  // Cachea con la misma queryKey que CommentsViewerPanel
  // para reusar transparentemente la data ya cargada.
  const commentsQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', versionId, 'comments'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listVersionComments(accessToken, artifactId, versionId)
    },
    // Cargar siempre que el artifact aún no sea canon — necesitamos
    // el count para deshabilitar Aprobar antes de cualquier voto.
    enabled: !!accessToken && !isCanon && stages.length > 0,
    staleTime: 15_000,
  })

  const openRootCount = useMemo(() => {
    const items = commentsQuery.data ?? []
    return items.filter(
      (c) =>
        c.kind === 'comment' &&
        c.review_cycle === reviewCycle &&
        c.status !== 'resolved',
    ).length
  }, [commentsQuery.data, reviewCycle])

  const allStagesDone =
    stages.length > 0 && stages.every((s) => s.state === 'done')
  // Doc 21 Fase 6 — dos estados visuales según etapa del flujo:
  //   - Antes de votar (hay stages pendientes) y con comments abiertos
  //     → banner "aprobación bloqueada" (pre-vote, explica por qué los
  //       botones de Aprobar están deshabilitados y cómo desbloquear).
  //   - Después de votar (todos los stages done) y con comments abiertos
  //     → banner "canon bloqueado" (post-vote, soft-block legacy).
  //
  // No filtramos por `isCanon` aquí: `artifact.is_canon` refleja v(N)
  // (canon vigente), pero la version bajo review puede ser v(N+1) que
  // todavía no es canon. La señal correcta es el estado del approval
  // flow (allStagesDone) + el count de comments abiertos del cycle.
  const showApproveBlockedBanner =
    !allStagesDone && openRootCount > 0
  const showCanonBlockedBanner =
    !isCanon && allStagesDone && openRootCount > 0
  // Doc 21 Fase 6 — por qué Aprobar está deshabilitado (tooltip de la fila).
  const approveBlockedReason =
    openRootCount > 0
      ? t(
          viewerIsExternal
            ? 'approval_panel.row.approve_blocked_tooltip_external'
            : 'approval_panel.row.approve_blocked_tooltip',
          { count: openRootCount },
        )
      : null

  // ─── Decide mutation (inline) ────────────────────────────────────────────
  const [decidingId, setDecidingId] = useState<string | null>(null)
  const [confirmReject, setConfirmReject] = useState<{
    approvalId: string
    comment: string
  } | null>(null)
  // Doc 21 Fase 6 — confirm también para aprobar (no solo rechazar).
  // Reusa <ConfirmDialog> del design system en lugar de window.confirm.
  const [confirmApprove, setConfirmApprove] = useState<{
    approvalId: string
  } | null>(null)

  async function handleDecide(
    approvalId: string,
    approve: boolean,
    comment?: string,
  ) {
    if (!accessToken) return
    setDecidingId(approvalId)
    try {
      await decideApproval(accessToken, approvalId, {
        approve,
        ...(comment ? { comment } : {}),
      })
      // Refresh approvals + artifact (status puede haber transicionado a
      // approved si fue el último voto).
      await queryClient.invalidateQueries({
        queryKey: [
          'artifacts',
          artifactId,
          'versions',
          versionId,
          'approvals',
        ],
      })
      await queryClient.invalidateQueries({
        queryKey: ['artifacts', artifactId],
      })
      onDecisionRecorded?.()
      pushToast({
        kind: 'success',
        message: approve
          ? t('approval_panel.decision.approved_toast')
          : t('approval_panel.decision.rejected_toast'),
      })
      setConfirmReject(null)
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null
      // El texto del backend le pide al aprobador subsanar y resolver los
      // comentarios, cosa que el `external` no puede hacer. El conteo pudo
      // cambiar desde que se cargó: se refresca para que aparezca el aviso.
      if (code === 'COMMENTS_OPEN_BLOCKING_APPROVAL' && viewerIsExternal) {
        await queryClient.invalidateQueries({
          queryKey: ['artifacts', artifactId, 'versions', versionId, 'comments'],
        })
        pushToast({
          kind: 'critical',
          message: t('approval_panel.decision.blocked_by_comments_external'),
          duration: 6000,
        })
        return
      }
      const fallback =
        err instanceof ApiError
          ? err.body?.message ?? err.message
          : err instanceof Error
            ? err.message
            : t('approval_panel.decision.error_toast')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      pushToast({ kind: 'critical', message, duration: 6000 })
    } finally {
      setDecidingId(null)
    }
  }

  if (approvalsQuery.isLoading) {
    return (
      <div className="rounded-md border border-line bg-surface px-4 py-3 flex items-center gap-2 text-xs text-ink-subtle">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t('approval_panel.loading')}
      </div>
    )
  }

  if (approvalsQuery.error) {
    return (
      <div className="rounded-md border border-critical/40 bg-critical-soft px-4 py-3 text-xs text-critical">
        {t('approval_panel.error')}
      </div>
    )
  }

  if (stages.length === 0) {
    return null
  }

  return (
    <div
      className="rounded-md border border-review/30 bg-review-soft/30 px-4 py-2.5"
    >
      <button
        type="button"
        onClick={() => setUserToggle(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="approval-panel-body"
        className="w-full flex items-center justify-between gap-3 text-left group"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="w-4 h-4 text-review shrink-0" />
          <h3 className="font-display text-sm font-semibold text-ink">
            {t('approval_panel.title')}
          </h3>
          {reviewCycle > 0 ? (
            <span
              className="px-1.5 py-0.5 rounded bg-surface-muted text-[10px] uppercase tracking-wider text-ink-subtle font-medium"
              title={t('approval_panel.cycle_badge_tooltip', {
                cycle: reviewCycle + 1,
              })}
            >
              {t('approval_panel.cycle_badge', { cycle: reviewCycle + 1 })}
            </span>
          ) : null}
          {hasPendingActionForMe && !isExpanded ? (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand-soft text-[10px] uppercase tracking-wider text-brand font-medium"
              title={t('approval_panel.action_required_tooltip')}
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-brand"
                aria-hidden="true"
              />
              {t('approval_panel.action_required')}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ProgressSummary stages={stages} t={t} />
          <span
            className="font-mono text-[10px] text-ink-subtle group-hover:text-ink transition-colors"
            aria-hidden="true"
          >
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
      </button>

      {!isExpanded ? null : (
      <div id="approval-panel-body" className="mt-3 pb-1.5 space-y-4">

      {/* Doc 21 Fase 6 — pre-vote: hay comments abiertos y todavía
          falta al menos un stage por decidir. Los botones de Aprobar
          están deshabilitados; este banner explica el porqué arriba
          de la lista de stages para que no quede dependiendo solo del
          tooltip del botón. */}
      {showApproveBlockedBanner ? (
        <div
          role="status"
          className="rounded-md border border-review/40 bg-review-soft/40 px-3 py-2.5 flex items-start gap-2.5"
        >
          <AlertCircle
            className="w-4 h-4 text-review shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-ink">
              {t('approval_panel.approve_blocked_banner.title', {
                count: openRootCount,
              })}
            </p>
            <p className="text-[11px] text-ink-muted mt-0.5">
              {t(
                viewerIsExternal
                  ? 'approval_panel.approve_blocked_banner.body_external'
                  : 'approval_panel.approve_blocked_banner.body',
              )}
            </p>
          </div>
        </div>
      ) : null}

      {/* Doc 21 Fase 6 — post-vote: todos los stages aprobaron pero
          quedan comments open del cycle activo. El backend ya persistió
          los votos; falta solo resolver los comments para que el
          auto-trigger complete la canonización. */}
      {showCanonBlockedBanner ? (
        <div
          role="status"
          className="rounded-md border border-high/40 bg-high-soft/40 px-3 py-2.5 flex items-start gap-2.5"
        >
          <AlertCircle
            className="w-4 h-4 text-high shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-ink">
              {t('approval_panel.canon_blocked_banner.title', {
                count: openRootCount,
              })}
            </p>
            <p className="text-[11px] text-ink-muted mt-0.5">
              {t('approval_panel.canon_blocked_banner.body')}
            </p>
          </div>
        </div>
      ) : null}

      <VersionObservationTrail
        groundings={groundingsQuery.data ?? []}
        sectionTitle={(sectionId, instanceKey) =>
          sectionInstanceTitle(sectionId, instanceKey, templateI18nMap)
        }
      />

      <ol className="space-y-3">
        {stages.map((stage, idx) => (
          <li key={stage.stage_order}>
            <StageBlock
              stage={stage}
              isLast={idx === stages.length - 1}
              currentUserId={currentUserId}
              canDecide={canDecide}
              decidingId={decidingId}
              approveBlockedReason={approveBlockedReason}
              onRequestApproveConfirm={(approvalId) =>
                setConfirmApprove({ approvalId })
              }
              onRequestRejectConfirm={(approvalId) =>
                setConfirmReject({ approvalId, comment: '' })
              }
            />
          </li>
        ))}
      </ol>

      {/* Issue #3 — Historial colapsable de ciclos previos. Se renderiza
          solo si hubo intentos previos (review_cycle > 0). El accordion
          hace lazy-fetch del summary cuando se expande. */}
      {reviewCycle > 0 ? (
        <ApprovalHistoryAccordion
          artifactId={artifactId}
          versionId={versionId}
          activeReviewCycle={reviewCycle}
          locale={locale}
        />
      ) : null}

      </div>
      )}

      {/* Doc 21 Fase 6 — approve confirm con tone="positive". El voto se
          persiste siempre; si quedan comments open, el artifact queda en
          estado "aprobado pero canon bloqueado" y el banner de arriba
          informa al user. Al resolver el último comment, el backend
          dispara auto-canonize sin re-voto. */}
      <ConfirmDialog
        open={!!confirmApprove}
        tone="positive"
        title={t('approval_panel.approve_dialog.title')}
        body={t('approval_panel.approve_dialog.body')}
        confirmLabel={t('approval_panel.approve_dialog.confirm')}
        cancelLabel={t('approval_panel.approve_dialog.cancel')}
        busy={
          confirmApprove !== null && decidingId === confirmApprove.approvalId
        }
        onCancel={() => setConfirmApprove(null)}
        onConfirm={async () => {
          if (!confirmApprove) return
          await handleDecide(confirmApprove.approvalId, true)
          setConfirmApprove(null)
        }}
      />

      {confirmReject ? (
        <RejectConfirmDialog
          comment={confirmReject.comment}
          onCommentChange={(c) =>
            setConfirmReject((prev) => (prev ? { ...prev, comment: c } : prev))
          }
          submitting={decidingId === confirmReject.approvalId}
          onCancel={() => setConfirmReject(null)}
          onConfirm={() =>
            handleDecide(confirmReject.approvalId, false, confirmReject.comment)
          }
        />
      ) : null}
    </div>
  )
}

// ─── ProgressSummary ────────────────────────────────────────────────────────
function ProgressSummary({
  stages,
  t,
}: {
  stages: StageGroup[]
  t: TFunction
}) {
  const done = stages.filter((s) => s.state === 'done').length
  const total = stages.length
  const hasRejected = stages.some((s) => s.state === 'rejected')

  if (hasRejected) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-critical">
        <XCircle className="w-3.5 h-3.5" />
        {t('approval_panel.summary.rejected')}
      </span>
    )
  }
  if (done === total) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-official">
        <ShieldCheck className="w-3.5 h-3.5" />
        {t('approval_panel.summary.complete')}
      </span>
    )
  }
  return (
    <span className="text-[11px] text-ink-subtle">
      {t('approval_panel.summary.progress', { done, total })}
    </span>
  )
}

// ─── StageBlock ─────────────────────────────────────────────────────────────
function StageBlock({
  stage,
  isLast,
  currentUserId,
  canDecide,
  decidingId,
  approveBlockedReason,
  onRequestApproveConfirm,
  onRequestRejectConfirm,
}: {
  stage: StageGroup
  isLast: boolean
  currentUserId: string | null
  canDecide: boolean
  decidingId: string | null
  /** Doc 21 Fase 6 — hard-block: con comments abiertos en el cycle activo,
   *  el motivo (texto del tooltip) deshabilita el botón Aprobar (no
   *  Rechazar) en cada row. `null` = no bloqueado. */
  approveBlockedReason: string | null
  onRequestApproveConfirm: (approvalId: string) => void
  onRequestRejectConfirm: (approvalId: string) => void
}) {
  const { t } = useTranslation('projects')

  const stateStyles = {
    done: 'bg-official-soft text-official',
    rejected: 'bg-critical-soft text-critical',
    cancelled: 'bg-surface-muted text-ink-subtle',
    active: 'bg-brand-soft text-brand',
    upcoming: 'bg-surface-muted text-ink-subtle',
  } as const

  return (
    <div className="flex items-stretch gap-3">
      <div className="flex flex-col items-center gap-1">
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-mono font-semibold ${stateStyles[stage.state]}`}
        >
          {stage.state === 'done' ? (
            <Check className="w-3.5 h-3.5" />
          ) : stage.state === 'rejected' ? (
            <X className="w-3.5 h-3.5" />
          ) : (
            stage.stage_order
          )}
        </div>
        {!isLast ? <div className="flex-1 w-px bg-line" /> : null}
      </div>

      <div className="flex-1 min-w-0 pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-sm font-semibold text-ink">
            {stage.stage_name}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
            {t(`approval_panel.state.${stage.state}` as const)}
          </span>
        </div>

        <ul className="mt-2 space-y-1.5">
          {stage.approvals.map((approval) => (
            <ApprovalRow
              key={approval.id}
              approval={approval}
              isMine={
                currentUserId !== null &&
                approval.approver_user_id === currentUserId
              }
              canDecide={canDecide}
              displayName={
                approval.approver_display_name ?? t('approval_panel.unknown_user')
              }
              roleLabel={approval.approver_role_label ?? approval.approver_role}
              isDeciding={decidingId === approval.id}
              approveBlockedReason={approveBlockedReason}
              onApprove={() => onRequestApproveConfirm(approval.id)}
              onReject={() => onRequestRejectConfirm(approval.id)}
            />
          ))}
        </ul>
      </div>
    </div>
  )
}

// ─── ApprovalRow ────────────────────────────────────────────────────────────
function ApprovalRow({
  approval,
  isMine,
  canDecide,
  displayName,
  roleLabel,
  isDeciding,
  approveBlockedReason,
  onApprove,
  onReject,
}: {
  approval: Approval
  isMine: boolean
  canDecide: boolean
  displayName: string
  /** Nombre legible del rol — OT-0013 §4.2: ya viene resuelto del
   *  backend (`approval.approver_role_label`), el caller solo hace
   *  fallback al `approver_role` raw si por algo llegara null. */
  roleLabel: string
  isDeciding: boolean
  approveBlockedReason: string | null
  onApprove: () => void
  onReject: () => void
}) {
  const { t, i18n } = useTranslation('projects')
  const isActiveAndPending =
    (approval.is_active ?? true) && approval.status === 'pending'
  const showActions = isMine && isActiveAndPending && canDecide
  // Doc 21 Fase 6 — hard-block: el botón Aprobar se deshabilita si la
  // version tiene comentarios abiertos en el cycle activo. Rechazar
  // queda libre (rechazo con comentarios es el flujo natural). El
  // backend duplica el gate; aquí es UX preventivo.
  const approveBlockedByComments = approveBlockedReason !== null
  const approveBlockedTooltip = approveBlockedReason ?? undefined

  return (
    <li className="rounded-md border border-line bg-surface px-3 py-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <StatusIcon status={approval.status} isActive={approval.is_active ?? true} />
          <span className="font-medium text-xs text-ink truncate">
            {displayName}
          </span>
          <span className="text-[10px] text-ink-subtle">·</span>
          <span className="text-[10px] text-ink-subtle">{roleLabel}</span>
          {isMine ? (
            <span className="px-1.5 py-0.5 rounded bg-brand-soft text-[9px] uppercase tracking-wider text-brand">
              {t('approval_panel.row.you')}
            </span>
          ) : null}
          {!isActiveAndPending && approval.is_active === false ? (
            <span className="text-[10px] text-ink-subtle italic">
              {t('approval_panel.row.upcoming')}
            </span>
          ) : null}
        </div>

        {showActions ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onReject}
              disabled={isDeciding}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-critical/40 text-critical text-[11px] hover:bg-critical-soft disabled:opacity-50"
            >
              <ThumbsDown className="w-3 h-3" />
              {t('approval_panel.row.reject')}
            </button>
            <button
              type="button"
              onClick={onApprove}
              disabled={isDeciding || approveBlockedByComments}
              title={approveBlockedTooltip}
              aria-disabled={isDeciding || approveBlockedByComments}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-official text-canvas text-[11px] hover:bg-official-deep disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeciding ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <ThumbsUp className="w-3 h-3" />
              )}
              {t('approval_panel.row.approve')}
            </button>
          </div>
        ) : approval.status !== 'pending' ? (
          <span className="text-[10px] text-ink-subtle">
            {approval.decision_at
              ? t('approval_panel.row.decided_at', {
                  when: new Date(approval.decision_at).toLocaleString(
                    toIntlLocale(i18n.language),
                  ),
                })
              : null}
          </span>
        ) : null}
      </div>

      {approval.comment ? (
        <p className="mt-2 text-[11px] text-ink-muted italic border-l-2 border-line pl-2">
          {approval.comment}
        </p>
      ) : null}
    </li>
  )
}

function StatusIcon({
  status,
  isActive,
}: {
  status: ApprovalStatus
  isActive: boolean
}) {
  if (status === 'approved')
    return <Check className="w-3.5 h-3.5 text-official shrink-0" />
  if (status === 'rejected')
    return <X className="w-3.5 h-3.5 text-critical shrink-0" />
  if (status === 'cancelled')
    return <XCircle className="w-3.5 h-3.5 text-ink-subtle shrink-0" />
  if (!isActive)
    return <Clock className="w-3.5 h-3.5 text-ink-subtle shrink-0" />
  return <Clock className="w-3.5 h-3.5 text-brand shrink-0" />
}

// ─── RejectConfirmDialog ────────────────────────────────────────────────────
function RejectConfirmDialog({
  comment,
  onCommentChange,
  submitting,
  onCancel,
  onConfirm,
}: {
  comment: string
  onCommentChange: (c: string) => void
  submitting: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('projects')
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel()
      }}
    >
      <div className="mx-4 w-full max-w-md rounded-md border border-line bg-surface shadow-lg">
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-critical" />
            <h3 className="font-display text-base font-semibold text-ink">
              {t('approval_panel.reject_dialog.title')}
            </h3>
          </div>
        </header>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-ink-muted">
            {t('approval_panel.reject_dialog.body')}
          </p>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
              {t('approval_panel.reject_dialog.comment_label')}
            </span>
            <textarea
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              disabled={submitting}
              rows={4}
              placeholder={t(
                'approval_panel.reject_dialog.comment_placeholder',
              )}
              className="mt-1 block w-full rounded-md border border-line bg-canvas px-3 py-2 text-xs text-ink focus:outline-none focus:border-brand disabled:opacity-50"
            />
          </label>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('approval_panel.reject_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-critical text-canvas text-sm font-medium rounded-md hover:bg-critical-deep disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <ThumbsDown className="w-3.5 h-3.5" />
            )}
            {t('approval_panel.reject_dialog.confirm')}
          </button>
        </footer>
      </div>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function groupApprovalsByStage(
  approvals: Approval[],
  locale: Locale,
  t: TFunction,
): StageGroup[] {
  const byStage = new Map<number, Approval[]>()
  for (const a of approvals) {
    const order = a.stage_order ?? 0
    if (!byStage.has(order)) byStage.set(order, [])
    byStage.get(order)!.push(a)
  }
  const orders = [...byStage.keys()].sort((a, b) => a - b)
  return orders.map((order) => {
    const items = byStage.get(order)!
    return {
      stage_order: order,
      stage_name: extractStageName(items[0], locale, t),
      approvals: items,
      state: aggregateState(items),
    }
  })
}

function extractStageName(
  sample: Approval | undefined,
  locale: Locale,
  t: TFunction,
): string {
  if (!sample) return ''
  const raw = sample.stage_name
  if (raw && typeof raw === 'object') {
    return (
      pickLocalized(raw, locale) ??
      t('approval_panel.stage_default', { order: sample.stage_order ?? 0 })
    )
  }
  if (typeof raw === 'string') return raw
  return t('approval_panel.stage_default', { order: sample.stage_order ?? 0 })
}

function aggregateState(items: Approval[]): StageGroup['state'] {
  // Doc 21 Fase 6 — cada submit cycle crea NUEVAS approval rows en BD
  // para el mismo (stage, approver_user_id); las del cycle anterior
  // quedan como rejected/cancelled como audit trail visible. Para
  // computar el estado VIGENTE del stage, agrupamos por approver y
  // tomamos la decisión MÁS RECIENTE de cada uno. Sin esto, una
  // rechazada histórica de cycle 0 "contaminaba" la badge del stage
  // aunque cycle N posterior haya aprobado.
  const latestByApprover = new Map<string, Approval>()
  for (const a of items) {
    const existing = latestByApprover.get(a.approver_user_id)
    if (!existing || isMoreRecent(a, existing)) {
      latestByApprover.set(a.approver_user_id, a)
    }
  }
  const latest = Array.from(latestByApprover.values())

  if (latest.some((a) => a.status === 'rejected')) return 'rejected'
  if (latest.every((a) => a.status === 'cancelled')) return 'cancelled'
  if (latest.every((a) => a.status === 'approved')) return 'done'
  if (latest.some((a) => (a.is_active ?? true) && a.status === 'pending'))
    return 'active'
  if (latest.every((a) => a.is_active === false)) return 'upcoming'
  return 'active'
}

/** Compara dos approvals y devuelve true si `a` es más reciente que `b`.
 *  Prefiere `decision_at` (cuando ya hay veredicto); cae a `created_at`
 *  cuando alguna todavía está pending. Ambos campos son ISO timestamps
 *  comparables string-wise. */
function isMoreRecent(a: Approval, b: Approval): boolean {
  const aTime = a.decision_at ?? a.created_at ?? ''
  const bTime = b.decision_at ?? b.created_at ?? ''
  return aTime > bTime
}

// ─── Historial accordion (Issue #3) ────────────────────────────────────────


/** Accordion colapsable que muestra el resumen de ciclos previos de
 *  aprobación. Se renderiza al final del panel cuando `reviewCycle > 0`
 *  (hubo al menos un rechazo + re-submit).
 *
 *  Lazy-fetch: la query a `/approval-history` se dispara SOLO al
 *  expandir, para no contaminar el render inicial con N+1 networking
 *  cuando el usuario solo viene a aprobar/rechazar.
 *
 *  Diseño: una línea por ciclo previo con avatar + estado + autor del
 *  veredicto + fecha localizada. El ciclo activo (que ya se ve arriba)
 *  se filtra. */
function ApprovalHistoryAccordion({
  artifactId,
  versionId,
  activeReviewCycle,
  locale,
}: {
  artifactId: string
  versionId: string
  activeReviewCycle: number
  locale: Locale
}) {
  const { t } = useTranslation('projects')
  const { accessToken } = useAccessToken()
  const [expanded, setExpanded] = useState(false)

  const historyQuery = useQuery({
    queryKey: [
      'artifacts',
      artifactId,
      'versions',
      versionId,
      'approval-history',
    ],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getApprovalHistory(accessToken, artifactId, versionId)
    },
    // Solo dispara fetch cuando el usuario abre el accordion.
    enabled: !!accessToken && expanded,
    staleTime: 60_000,
  })

  // Filtra el cycle activo — ese ya se renderiza arriba.
  const previousCycles = (historyQuery.data ?? []).filter(
    (s) => s.review_cycle !== activeReviewCycle,
  )

  const previousCount = activeReviewCycle  // 0-indexed → cycle N implica N intentos previos.

  const intlLocale = toIntlLocale(locale)
  const formatDateTime = (iso: string | null): string => {
    if (!iso) return '—'
    try {
      return new Intl.DateTimeFormat(intlLocale, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(iso))
    } catch {
      return iso
    }
  }

  return (
    <div className="border-t border-line/60 pt-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-2 text-[11px] text-ink-muted hover:text-ink transition-colors"
      >
        <span className="font-medium">
          {t('approval_panel.history.toggle', { count: previousCount })}
        </span>
        <span
          className="font-mono text-[10px]"
          aria-hidden="true"
        >
          {expanded ? '▼' : '▶'}
        </span>
      </button>

      {expanded ? (
        <div className="mt-3">
          {historyQuery.isLoading ? (
            <div className="flex items-center gap-2 text-[11px] text-ink-subtle py-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t('approval_panel.history.loading')}
            </div>
          ) : historyQuery.error ? (
            <p className="text-[11px] text-critical py-2">
              {t('approval_panel.history.error')}
            </p>
          ) : previousCycles.length === 0 ? (
            <p className="text-[11px] text-ink-subtle italic py-2">
              {t('approval_panel.history.empty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {previousCycles.map((cycle) => {
                const isRejected = cycle.status === 'rejected'
                const isApproved = cycle.status === 'approved'
                const Icon = isRejected
                  ? X
                  : isApproved
                    ? Check
                    : Clock
                const iconColor = isRejected
                  ? 'text-critical'
                  : isApproved
                    ? 'text-official'
                    : 'text-ink-subtle'
                const userName =
                  cycle.decided_by_display_name ??
                  cycle.decided_by_user_id?.slice(0, 8) ??
                  null
                const roleLabel =
                  cycle.decided_by_role_label ?? cycle.decided_by_role
                return (
                  <li
                    key={cycle.review_cycle}
                    className="flex items-start gap-2 text-[11px]"
                  >
                    <Icon
                      className={`w-3 h-3 shrink-0 mt-0.5 ${iconColor}`}
                      aria-hidden="true"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-ink">
                        <span className="font-medium">
                          {t('approval_panel.history.cycle_label', {
                            number: cycle.review_cycle + 1,
                          })}
                        </span>
                        <span className="text-ink-subtle">
                          {' · '}
                          {isRejected
                            ? t('approval_panel.history.rejected_by', {
                                name: userName ?? '—',
                                role: roleLabel ?? '—',
                              })
                            : isApproved
                              ? t('approval_panel.history.approved')
                              : t('approval_panel.history.in_progress')}
                        </span>
                        {cycle.decided_at ? (
                          <span className="text-ink-subtle">
                            {' · '}
                            {formatDateTime(cycle.decided_at)}
                          </span>
                        ) : null}
                      </p>
                      {cycle.comment ? (
                        <p className="mt-0.5 text-ink-muted italic">
                          “{cycle.comment}”
                        </p>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}
